/**
 * The six-state client sign-off model for a project phase. The Scoping-view
 * Sign-off Status column renders each of these as a single label per phase; other
 * client-workspace surfaces can key off the same enum.
 *
 * A phase's status is derived from four signals:
 *
 *   1. Whether the phase requires a sign-off at all (portalConfig.requiresClientSignoff).
 *   2. Whether the phase has been formally locked (portalConfig.signoffLocked,
 *      backed by the signoff_locked_at timestamp).
 *   3. Whether every task in the phase has landed in a done state — see
 *      `arePhaseTasksComplete`.
 *   4. Where the outstanding sign-off REQUEST sits in its lifecycle
 *      (`PhaseSignoffRequestState`).
 *
 * ── ON THE REQUEST LIFECYCLE ────────────────────────────────────────────────
 * Nothing produces signal 4 yet. The client-workspace database has no sign-off
 * request entity at all: the phase config table carries only
 * `requires_client_signoff`, `signoff_approver_portal_user_id` and the (still
 * unwritten) `signoff_locked_at`; `pendingSignoffCountsByProject` in the portal
 * backend's signoffPendingRepo is a stub that correctly answers zero for the same
 * reason. So AWAITING_CLIENT and CHANGES_REQUESTED are unreachable in production
 * today — not because this helper cannot express them, but because no row anywhere
 * can say a request was sent.
 *
 * They are modelled and tested here rather than left out so that (a) the renderer is
 * proven for all six states, and (b) when the request entity lands, the only change
 * is a producer passing `requestState` — no renderer, label map or enum touched.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The transition from REQUIRED_BUT_INCOMPLETE to READY_TO_SEND when the last
 * outstanding task closes is automatic and intentional: sign-off becomes available
 * the moment work is done, with no separate "mark ready" step. If the product
 * decision later switches to an explicit gate, this file is the one place to add it.
 */

export enum PhaseSignoffStatus {
	NOT_CONFIGURED = 'NOT_CONFIGURED',
	REQUIRED_BUT_INCOMPLETE = 'REQUIRED_BUT_INCOMPLETE',
	READY_TO_SEND = 'READY_TO_SEND',
	AWAITING_CLIENT = 'AWAITING_CLIENT',
	CHANGES_REQUESTED = 'CHANGES_REQUESTED',
	SIGNED_OFF = 'SIGNED_OFF',
}

/**
 * Where the phase's sign-off request sits. NONE covers both "never sent" and
 * "resolved", because a resolved request means the phase is locked and
 * SIGNED_OFF wins regardless.
 */
export enum PhaseSignoffRequestState {
	NONE = 'NONE',
	SENT = 'SENT',
	CHANGES_REQUESTED = 'CHANGES_REQUESTED',
}

export type PhaseSignoffDerivationInput = {
	requiresClientSignoff: boolean | null | undefined;
	signoffLocked: boolean | null | undefined;
	/**
	 * True when every task in the phase is done. A phase with zero tasks is
	 * trivially complete, so a milestone with nothing to do is READY_TO_SEND
	 * rather than stuck on REQUIRED_BUT_INCOMPLETE forever. Build this with
	 * `arePhaseTasksComplete` rather than by hand.
	 */
	tasksComplete: boolean;
	/**
	 * Lifecycle of the outstanding request. Optional, and nothing supplies it
	 * today — see the module header.
	 */
	requestState?: PhaseSignoffRequestState | null;
};

const asBool = (value: boolean | null | undefined): boolean => value === true;

/**
 * Map the portal config + task-completion signal for one phase to its
 * PhaseSignoffStatus. Pure and side-effect-free; safe to call in render.
 *
 * Precedence, highest first: locked beats everything (a signed-off phase stays
 * signed off even if someone reopens a task); then a request that is actually out
 * with the client, since "Awaiting client" must never be displaced by a task
 * churning back to open; then the task-completion split.
 */
export function derivePhaseSignoffStatus(input: PhaseSignoffDerivationInput): PhaseSignoffStatus {
	if (!asBool(input.requiresClientSignoff)) return PhaseSignoffStatus.NOT_CONFIGURED;
	if (asBool(input.signoffLocked)) return PhaseSignoffStatus.SIGNED_OFF;
	if (input.requestState === PhaseSignoffRequestState.CHANGES_REQUESTED) return PhaseSignoffStatus.CHANGES_REQUESTED;
	if (input.requestState === PhaseSignoffRequestState.SENT) return PhaseSignoffStatus.AWAITING_CLIENT;
	if (!input.tasksComplete) return PhaseSignoffStatus.REQUIRED_BUT_INCOMPLETE;
	return PhaseSignoffStatus.READY_TO_SEND;
}

/**
 * Whether every task rolled up under this phase is done, from the same
 * `doneCount` / `taskCount` totals the table already aggregates for its
 * done-percentage cell (`TableUtil.getRollUpValuesForPhase`).
 *
 * Counts, not the progress percentage: under manual progress the percentage is a
 * number a PM typed, so a phase could read 100% with tasks still open — and the
 * acceptance criterion is "every task in the phase is complete", not "progress
 * reports 100".
 *
 * A phase with no tasks counts as complete, so an empty milestone is READY_TO_SEND
 * rather than permanently REQUIRED_BUT_INCOMPLETE. Missing totals (a caller whose
 * rollup has not resolved) are treated the same way as an empty phase.
 */
export function arePhaseTasksComplete(rolledUpValues: {taskCount?: number; doneCount?: number} | null | undefined): boolean {
	const taskCount = rolledUpValues?.taskCount;
	const doneCount = rolledUpValues?.doneCount;
	if (typeof taskCount !== 'number' || !Number.isFinite(taskCount) || taskCount <= 0) return true;
	if (typeof doneCount !== 'number' || !Number.isFinite(doneCount)) return false;
	return doneCount >= taskCount;
}

/**
 * The label for the client-workspace user who signs a phase off. The portal users
 * table carries no name column, so the email IS the display name.
 *
 * The phase stores a portal_users UUID; the eligible-approver list is the only
 * thing that can turn it into something human-readable. An approver who has since
 * lost eligibility is absent from that list, so this returns null and the caller
 * falls back to the unnamed badge rather than printing a bare UUID.
 */
export function resolveSignoffApproverLabel(
	portalUserId: string | null | undefined,
	approvers: ReadonlyArray<{portalUserId?: string | null; email?: string | null}> | null | undefined
): string | null {
	if (!portalUserId) return null;
	const match = (approvers || []).find(approver => approver && approver.portalUserId === portalUserId);
	return match && match.email ? match.email : null;
}

/**
 * The translation-id string used to render each state's label. Kept in the
 * same file as the enum so a new state can never ship without a label.
 */
export const PHASE_SIGNOFF_STATUS_LABEL_ID: Record<PhaseSignoffStatus, string> = {
	[PhaseSignoffStatus.NOT_CONFIGURED]: 'client_portal.signoff_state.not_configured',
	[PhaseSignoffStatus.REQUIRED_BUT_INCOMPLETE]: 'client_portal.signoff_state.required_but_incomplete',
	[PhaseSignoffStatus.READY_TO_SEND]: 'client_portal.signoff_state.ready_to_send',
	[PhaseSignoffStatus.AWAITING_CLIENT]: 'client_portal.signoff_state.awaiting_client',
	[PhaseSignoffStatus.CHANGES_REQUESTED]: 'client_portal.signoff_state.changes_requested',
	[PhaseSignoffStatus.SIGNED_OFF]: 'client_portal.signoff_state.signed_off',
};
